import { useCollectionStore } from "~/composables/useCollectionStore";
import type { RouteLocationRaw } from "vue-router";
import type { LoadMoreIds } from "./useCollectionStore";

export type NavigationTarget = "talents" | "pay-tracking";

export const useNavigation = (target: NavigationTarget) => {
  const { talentsStore, payTrackingStore } = useCollectionStore();

  const store = computed(() => {
    switch (target) {
      case "talents":
        return talentsStore;
      case "pay-tracking":
        return payTrackingStore;
      default:
        throw new Error(`Unknown navigation target: ${target}`);
    }
  });

  const setStore = (ids: string[], loadMore?: LoadMoreIds) => {
    store.value.set(ids, loadMore);
  };

  const loadMoreIfNearEnd = async () => {
    const currentId = store.value.getCurrent();
    if (!currentId) return;
    await store.value.loadMoreIfNearEnd(currentId);
  };

  const clearStore = () => {
    store.value.clear();
  };

  const previous = computed(() => {
    const currentId = store.value.getCurrent();
    if (!currentId) return null;
    return store.value.getPrevious(currentId);
  });

  const next = computed(() => {
    const currentId = store.value.getCurrent();
    if (!currentId) return null;
    return store.value.getNext(currentId);
  });

  return {
    setStore,
    clearStore,
    loadMoreIfNearEnd,
    previous,
    next,
  };
};

export const toSinglePage = (route: RouteLocationRaw): RouteLocationRaw => {
  const currentRoute = useRoute();

  if (typeof route === "string") return { path: route, query: { from: currentRoute.fullPath } };
  return { ...route, query: { ...(route.query ?? {}), from: currentRoute.fullPath } };
};
